import React, { useRef } from "react";
import config from "../../../contexts/ConfigContext";
import SearchTags, { SearchTagHandle } from "../../../components/SearchTags";
import { Row, Col } from "react-bootstrap";
import { Trans } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import {
  GridColumnMenuProps,
  GridColumnMenuFilter
} from "@progress/kendo-react-grid";
import { ODataProvider } from "../../../contexts/ODataContext";
import { LabelService } from "../../../services/LabelService";
import { MultiSelectChangeEvent } from "@progress/kendo-react-dropdowns";
import DataGridCustom, { ColumnCustomProps, DataGridHandle } from "../../../components/DataGridCustom";
import { clockIcon } from "@progress/kendo-svg-icons";
import { FilterModel } from "../../../services/LabelService";
import { Link } from "react-router-dom";
import Modal from 'react-bootstrap/Modal';
import { SvgIcon } from "@progress/kendo-react-common";
import * as svgIcons from "@progress/kendo-svg-icons";
import { Button } from "@progress/kendo-react-buttons";
import { CommonLibrary } from "../../../contexts/CommonContext";
import { PermissionButton } from "../../../components/PermissionButton";
import { useNotification } from "contexts/NotificationContext";
import { AuditEntryModal } from "components/History Tracking/AuditComponent";

const LabelSearch: React.FC = () => {

  // Define properties and hooks
  const labelService = new LabelService();
  const commonLibrary = new CommonLibrary();
  const notifications = useNotification();
  const { t } = useTranslation();
  const API_URL = config.API_URL;
  const searchList = {
    Label: t("general.search"),
    PlaceHolder: t("general.searchByTags"),
    TextField: "Description",
    KeyField: "Id",
    PageSize: 10,
    baseUrl: API_URL + "/odata/Tags"
  }

  const labelApiUrl = config.API_URL + "/odata/ProjectLabels/";
  const [value, setValue] = React.useState<any[]>([]);
  const [filterType, setFilterType] = React.useState<number>(1);
  const dataGridRef = useRef<DataGridHandle>(null);
  const searchTagRef = useRef<SearchTagHandle>(null);
  const [selectedItems, setSelectedItems] = React.useState<number[]>([]);
  const [show, setShow] = React.useState(false);
  const [showAuditHistory, setShowAuditHistory] = React.useState(false);
  const [auditHistory, setAuditHistory] = React.useState<any>([]);
  const [auditTitle, setAuditTitle] = React.useState<any>(null);

  const handleClose = () => setShow(false);

  const openAuditHistory = (labelId: number, title?: string | null) => {
    labelService.GetAudit(labelId).then((result) => {
      setAuditHistory(result);
      setAuditTitle(title);
      setShowAuditHistory(true);
    })
    .catch((error) => {
      notifications.showNotification("Failed to load label audit history", error);
    });
  } 

  const closeAuditHistoryWindow = () => {
    setShowAuditHistory(false);
    setAuditHistory([]);
    setAuditTitle(null);
  };

  // Define the column menu
  const ColumnMenu = (props: GridColumnMenuProps) => {
    return (
      <div>
        <GridColumnMenuFilter {...props} expanded={true} />
      </div>
    );
  };
  // Define the columns
  const commandCell = (props: any) => (
    <td style={{ textAlign: "center" }}>
      <span className="button-grid">
        <PermissionButton code="configuration_labels_levels" mode="edit">
          <Link to="/labels/manageLabel" className="k-button k-button-sm k-button-outline k-button-outline-secondary k-rounded-sm" state={props.dataItem}><Trans>general.manageLevel</Trans></Link>
        </PermissionButton>
        <PermissionButton code="audit_history_labels" mode="edit" visible={false}>
          <Button className="k-button k-button-sm k-button-outline k-rounded-sm" themeColor={"light"} title={t("Audit.labelHistoryToolTip")} style={{ marginLeft: "8px", height: "30px", borderColor: "#000", color: "#000" }} onClick={() => openAuditHistory(props.dataItem.LabelId, props.dataItem.Description)}>
            <SvgIcon icon={clockIcon} />
          </Button>
        </PermissionButton>
      </span>
    </td>
  );

  const columns = [] as ColumnCustomProps[];
  columns.push({ field: "Description", title: t("general.name"), columnMenu: ColumnMenu, isExportable: true });
  columns.push({ field: "ParentDescription", title: t("general.parent"), columnMenu: ColumnMenu, isExportable: true });
  columns.push({ field: "Path", title: t("configuration.labels.path"), columnMenu: ColumnMenu, isExportable: true });
  columns.push({ field: "Tags", title: t("general.tags"), columnMenu: undefined, isExportable: true });
  columns.push({ cell: commandCell, width: "200px", isExportable: false });




  // Define the event handlers
  const onClickSearch = () => {
    labelService.UpdateMyProjectLabelsFilter({ Tags: value.map(a => a.Description), WhereType: filterType }).then(() => {
      dataGridRef.current?.refreshData();
    });
  }

  const onFilterTypeChange = (filterType: number) => {
    setFilterType(filterType);
  }

  const onSetValue = (event: MultiSelectChangeEvent) => {
    setValue(event.target.value);
  }

  const loadFilters = () => {
    labelService.GetMyProjectLabelsFilter().then((data: FilterModel) => {
      setFilterType(data.WhereType);
      setValue(data.Tags);
      searchTagRef.current?.setValueFilter(data.Tags, data.WhereType);
    });
  }

  const handleRemoveLabelFromProject = () => {
    if (selectedItems.length > 0) {
      setShow(true);
    }
  }

  const handleConfirm = () => {
    setShow(false);
    const labelModel = { ProjectId: commonLibrary.ProjectId, ObjectIds: selectedItems };
    labelService.RemoveLabelsFromProject(labelModel).then(() => {
      dataGridRef.current?.refreshData();
    });
  }

  const onSelectedItemsChange = (selectedItems: number[]) => {
    setSelectedItems(selectedItems);
  }

  React.useEffect(() => {
    loadFilters();
  }, []);

  return (
    <div>
      <div className="pt-4">
        <div className="tile-content">
          <h5 className="title"><Trans>configuration.labels.labelSearch</Trans></h5>
          <Row className="pt-4">
            <Col sm={8} >
              <Row>
                <Col sm={7}>
                  <ODataProvider baseUrl={searchList.baseUrl} loadingContent=".k-multiselect">
                    <SearchTags ref={searchTagRef} KeyField={searchList.KeyField} Label={searchList.Label} PageSize={searchList.PageSize} PlaceHolder={searchList.PlaceHolder} TextField={searchList.TextField} onFilterTypeChange={onFilterTypeChange} onSetValue={onSetValue} />
                  </ODataProvider>
                </Col>
                <Col sm={3} className="pt-4">
                  <Button themeColor="secondary" onClick={onClickSearch}>
                  <SvgIcon className="btn-icons" icon={svgIcons.searchIcon} size="medium" /><Trans>general.search</Trans></Button>
                </Col>
              </Row>
            </Col>
            <Col sm={{ span: 2, offset: 2 }}>
              <Col sm={12} className="pt-4">
                <PermissionButton code="menu_labels" mode="create">
                  <Link to="/labels/addLabelsToProject" className="btn btn-primary" ><Trans>configuration.labels.addLabelsToProject</Trans></Link>
                </PermissionButton>                
              </Col>
            </Col>
          </Row>
        </div>
      </div>
      <div className="pt-4">
        <div className="tile-content">
          <Row>
            <Col sm={12}>
              <ODataProvider baseUrl={labelApiUrl} loadingContent=".k-grid-content">
                <DataGridCustom PermissionButtonCode="menu_labels" ShowRemoveButton={true} onRemoveButtonClick={handleRemoveLabelFromProject} ref={dataGridRef} DataItemKey="LabelId" Columns={columns} onSelectedItemsChange={onSelectedItemsChange} />
              </ODataProvider>
            </Col>
          </Row>
        </div>
      </div>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title><Trans>general.titleRemoveMessage</Trans></Modal.Title>
        </Modal.Header>
        <Modal.Body><Trans>general.removeMessage</Trans></Modal.Body>
        <Modal.Footer>
          <Button themeColor="secondary" onClick={handleClose}>
            <Trans>general.cancel</Trans>
          </Button>
          <Button themeColor="primary" onClick={handleConfirm}>
            <Trans>general.ok</Trans>
          </Button>
        </Modal.Footer>
      </Modal>
      {showAuditHistory && (
      <AuditEntryModal onClose={closeAuditHistoryWindow} auditEntriesModel={auditHistory} entityTitleName={auditTitle}/>
      )}
    </div>
  );
};

export default LabelSearch;
